import type { ClientListResponse } from '@oneup/shared';
import { authedFetch } from '../auth/supabase-browser';
import { apiUrl } from './client-modules';
import { withClientId } from '../organization/OrganizationProvider';

export async function fetchClientList<T>(
  path: string,
  clientId: string,
  options: { cursor?: string | null; limit?: number; signal?: AbortSignal } = {},
): Promise<ClientListResponse<T>> {
  const scopedPath = withClientId(path, clientId);
  const queryIndex = scopedPath.indexOf('?');
  const pathname = queryIndex >= 0 ? scopedPath.slice(0, queryIndex) : scopedPath;
  const query = new URLSearchParams(queryIndex >= 0 ? scopedPath.slice(queryIndex + 1) : '');
  query.set('limit', String(options.limit ?? query.get('limit') ?? 50));
  if (options.cursor) query.set('cursor', options.cursor);
  return request<ClientListResponse<T>>(`${pathname}?${query}`, options.signal);
}

export async function fetchClientDetail<T>(path: string, clientId: string): Promise<T> {
  return request<T>(withClientId(path, clientId));
}

export async function fetchClientBlob(path: string, clientId: string): Promise<Blob> {
  const response = await authedFetch(apiUrl(withClientId(path, clientId)), {
    headers: { Accept: '*/*' },
  });
  if (!response.ok) {
    throw new Error(
      response.status === 403 || response.status === 404
        ? 'You do not have access to this attachment.'
        : 'We couldn’t load that attachment just now.',
    );
  }
  return response.blob();
}

/**
 * One organization-scoped GET returning whatever shape the endpoint defines.
 *
 * `fetchClientList` assumes the paginated list envelope and `fetchClientDetail`
 * reads as "one record"; endpoints like Alice usage return neither.
 */
export async function fetchClientJson<T>(
  path: string,
  clientId: string,
  signal?: AbortSignal,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(withClientId(path, clientId), signal, headers);
}

/** POST an organization-scoped JSON body and return the parsed response. */
export async function postClientJson<T>(
  path: string,
  clientId: string,
  body: Record<string, unknown>,
  idempotencyKey?: string,
): Promise<T> {
  const response = await authedFetch(apiUrl(withClientId(path, clientId)), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    // The Worker's budget message already names the ceiling and reset time, so
    // surface it verbatim rather than replacing it with something generic.
    throw new ClientRequestError(
      error?.message ?? 'We couldn’t complete that just now. Please try again in a moment.',
      error?.code,
      response.status,
    );
  }
  return payload as T;
}

/** PATCH an organization-scoped JSON resource and return its response. */
export async function patchClientJson<T>(
  path: string,
  clientId: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await authedFetch(apiUrl(withClientId(path, clientId)), {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ClientRequestError(
      error?.message ?? 'We couldn’t save that just now. Please try again in a moment.',
      error?.code,
      response.status,
    );
  }
  return payload as T;
}

export class ClientRequestError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ClientRequestError';
  }
}

export async function mutateClientDetail(
  path: string,
  clientId: string,
  body?: Record<string, unknown>,
): Promise<void> {
  const response = await authedFetch(apiUrl(withClientId(path, clientId)), {
    method: 'POST',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message ?? 'We couldn’t complete that just now. Please try again in a moment.');
  }
}

async function request<T>(path: string, signal?: AbortSignal, headers?: Record<string, string>): Promise<T> {
  const response = await authedFetch(apiUrl(path), {
    headers: { Accept: 'application/json', ...headers },
    signal,
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const code = body && typeof body === 'object' && 'error' in body
      ? (body as { error?: { code?: string } }).error?.code
      : undefined;
    const message = code === 'source_not_configured' || code === 'client_not_configured'
      ? 'This part of your workspace isn’t connected yet. Let One Up know and we’ll finish setting it up.'
      : response.status === 401
        ? 'Your session has expired. Please sign in again.'
        : response.status === 403
          ? 'You don’t have access to this workspace.'
          : 'We couldn’t load that just now. Please try again in a moment.';
    throw new ClientRequestError(message, code, response.status);
  }
  return body as T;
}
